import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { adminOrderService, vendorService } from '../services'
import { getArticleCode } from '../utils/articleCode'
import { getLineRemainingToShip } from '../utils/shipmentAvailabilityWarning'
import { parseShipmentError } from '../utils/shipmentErrors'
import { normalizeManualSalesOrder } from '../utils/manualSalesOrder'
import { buildDsdShipmentItems } from '../utils/dsdShipment'
import {
  formatSellingPriceExceedsMrpError,
  validatePositiveSellingPriceAndMrp,
} from '../utils/sellingPriceMrp'
import ManualSoNumberFields from './ManualSoNumberFields'
import ShipmentVatReviewDialog from './ShipmentVatReviewDialog'
import {
  DistributionCenterField,
  usePaginatedLocationSearch,
} from './shipmentLocationPicker'
import { buildShipmentVatReviewRows } from '../utils/shipmentVatReview'

function normalizeShipQty(raw) {
  if (raw === '' || raw === '-') return raw
  const num = Number(raw)
  if (!Number.isFinite(num)) return '0'
  return String(Math.max(0, Math.floor(num)))
}

function formatVendorLabel(vendor) {
  if (!vendor) return ''
  const id = vendor.vendor_id || ''
  const name = vendor.vendor_name || ''
  if (name && id) return `${name} (${id})`
  return name || id
}

/**
 * Dedicated DSD shipment dialog: DSD lines only + mandatory vendor + location +
 * optional manual SO. Submits via the existing createShipment / dispatch APIs.
 */
export default function DsdShipmentDialog({
  open,
  onClose,
  order,
  dsdLines = [],
  initialQtyByLine = {},
  initialPendingAdds = [],
  initialLocation = null,
  initialSoNumber = '',
  initialSoReason = '',
  canEnterManualSo = false,
  // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
  externalIntegrationEnabled = true,
  onProductVatUpdated,
  onSuccess,
}) {
  const [qtyByLine, setQtyByLine] = useState({})
  const [pendingAdds, setPendingAdds] = useState([])
  const [vendorOptions, setVendorOptions] = useState([])
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [vendorInput, setVendorInput] = useState('')
  const [vendorLoading, setVendorLoading] = useState(false)
  const [vendorError, setVendorError] = useState(null)
  const [soNumber, setSoNumber] = useState('')
  const [soReason, setSoReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [vatReviewOpen, setVatReviewOpen] = useState(false)
  const [vatReviewRows, setVatReviewRows] = useState([])
  const [vatReviewError, setVatReviewError] = useState(null)

  const locationPicker = usePaginatedLocationSearch({
    open,
    onError: setError,
  })
  const {
    options: locations,
    selected: selectedLocation,
    locationId,
    inputValue: locationInputValue,
    loading: locationsLoading,
    loadingMore: locationsLoadingMore,
    resetSelection: resetLocationSelection,
    selectLocation,
    handleInputChange: handleLocationInputChange,
    handleListScroll: handleLocationListScroll,
  } = locationPicker

  useEffect(() => {
    if (!open) return
    const nextQty = {}
    for (const li of dsdLines) {
      const remaining = getLineRemainingToShip(li)
      const seeded = Number(initialQtyByLine[li.id])
      const qty =
        Number.isFinite(seeded) && seeded > 0
          ? seeded
          : remaining > 0
            ? remaining
            : 0
      nextQty[li.id] = String(qty)
    }
    setQtyByLine(nextQty)
    setPendingAdds(
      (initialPendingAdds || []).map((add) => ({
        ...add,
        quantity: String(add.quantity ?? '0'),
      })),
    )
    setSelectedVendor(null)
    setVendorInput('')
    setVendorError(null)
    setSoNumber(canEnterManualSo ? String(initialSoNumber || '') : '')
    setSoReason(canEnterManualSo ? String(initialSoReason || '') : '')
    setError(null)
    setVatReviewOpen(false)
    setVatReviewRows([])
    setVatReviewError(null)
    resetLocationSelection()
    if (initialLocation) {
      selectLocation(initialLocation)
    }
  }, [
    open,
    order?.order_id,
    dsdLines,
    initialQtyByLine,
    initialPendingAdds,
    initialLocation,
    initialSoNumber,
    initialSoReason,
    canEnterManualSo,
    resetLocationSelection,
    selectLocation,
  ])

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    const timer = setTimeout(() => {
      ;(async () => {
        try {
          setVendorLoading(true)
          setVendorError(null)
          const data = await vendorService.list({
            page: 1,
            limit: 100,
            ...(vendorInput.trim() ? { search: vendorInput.trim() } : {}),
          })
          if (!cancelled) {
            setVendorOptions(Array.isArray(data?.items) ? data.items : [])
          }
        } catch (err) {
          if (!cancelled) {
            setVendorOptions([])
            setVendorError(
              err?.response?.data?.message ||
                err?.message ||
                'Could not load vendors',
            )
          }
        } finally {
          if (!cancelled) setVendorLoading(false)
        }
      })()
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, vendorInput])

  const manualSo = canEnterManualSo
    ? normalizeManualSalesOrder(soNumber, soReason)
    : { soNumber: '', reason: '', error: null, active: false }

  const positiveCount = useMemo(() => {
    const lineQty = dsdLines.reduce(
      (sum, li) => sum + (Number(qtyByLine[li.id]) || 0),
      0,
    )
    const addQty = pendingAdds.reduce(
      (sum, add) => sum + (Number(add.quantity) || 0),
      0,
    )
    return lineQty + addQty
  }, [dsdLines, qtyByLine, pendingAdds])

  const canSubmit =
    !submitting &&
    !!locationId &&
    !!selectedVendor?.vendor_id &&
    positiveCount > 0 &&
    !manualSo.error

  const executeCreate = useCallback(async () => {
    // After VAT review Continue, keep failures on that dialog (last step).
    const surfaceOnVatReview = vatReviewOpen
    const reportSubmitError = (message) => {
      if (surfaceOnVatReview) {
        setVatReviewError(message)
        setError(null)
      } else {
        setError(message)
        setVatReviewError(null)
      }
    }

    const items = buildDsdShipmentItems({
      lines: dsdLines,
      qtyByLine,
      pendingAdds,
    })
    if (!items.length) {
      reportSubmitError('Enter at least one DSD quantity greater than zero.')
      return false
    }

    for (const add of pendingAdds) {
      const qty = Number(add.quantity) || 0
      if (qty <= 0) continue
      const priceError = validatePositiveSellingPriceAndMrp(
        Number(add.unit_price),
        Number(add.mrp),
        { requireBoth: true },
      )
      if (priceError) {
        reportSubmitError(
          `${add.product_name || 'Added product'}: ${priceError}`,
        )
        return false
      }
    }

    setSubmitting(true)
    setError(null)
    setVatReviewError(null)
    try {
      const locNum = Number(locationId)
      const options = {
        vendorId: selectedVendor.vendor_id,
        dsdShipment: true,
        ...(manualSo.active
          ? { soNumber: manualSo.soNumber, reason: manualSo.reason }
          : {}),
      }
      const shipFn =
        order.order_status === 'READY_FOR_DISPATCH'
          ? adminOrderService.dispatchOrder
          : adminOrderService.createShipment
      await shipFn(order.order_id, items, null, locNum, options)
      setVatReviewOpen(false)
      setVatReviewError(null)
      onSuccess?.()
      onClose()
      return true
    } catch (err) {
      reportSubmitError(parseShipmentError(err))
      return false
    } finally {
      setSubmitting(false)
    }
  }, [
    order?.order_id,
    order?.order_status,
    locationId,
    selectedVendor,
    manualSo,
    dsdLines,
    qtyByLine,
    pendingAdds,
    vatReviewOpen,
    onSuccess,
    onClose,
  ])

  const handleSubmit = useCallback(async () => {
    if (!order?.order_id || submitting) return
    if (!locationId) {
      setError(
        'Select a distribution center before creating the Fulfillment Center assignment.',
      )
      return
    }
    if (!selectedVendor?.vendor_id) {
      setError(
        'Select a vendor before creating the Fulfillment Center assignment.',
      )
      return
    }
    if (manualSo.error) {
      setError(manualSo.error)
      return
    }

    const items = buildDsdShipmentItems({
      lines: dsdLines,
      qtyByLine,
      pendingAdds,
    })
    if (!items.length) {
      setError('Enter at least one DSD quantity greater than zero.')
      return
    }

    // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
    if (externalIntegrationEnabled) {
      setVatReviewRows(
        buildShipmentVatReviewRows({
          lines: dsdLines,
          qtyByLine,
          pendingAdds,
        }),
      )
      setError(null)
      setVatReviewError(null)
      setVatReviewOpen(true)
      return
    }

    await executeCreate()
  }, [
    order?.order_id,
    submitting,
    locationId,
    selectedVendor,
    manualSo,
    dsdLines,
    qtyByLine,
    pendingAdds,
    externalIntegrationEnabled,
    executeCreate,
  ])

  const handleVatUpdated = useCallback(
    (productId, vat) => {
      const idKey = String(productId || '').trim()
      if (!idKey) return
      setPendingAdds((prev) =>
        prev.map((add) =>
          String(add.admin_product_id) === idKey ? { ...add, vat } : add,
        ),
      )
      onProductVatUpdated?.(idKey, vat)
    },
    [onProductVatUpdated],
  )

  return (
    <>
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Fulfillment Center (DSD)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This Fulfillment Center assignment includes only Direct Store Delivery
          products. Choose a vendor and distribution center, then set quantities
          to ship.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {vendorError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {Array.isArray(vendorError) ? vendorError.join(', ') : vendorError}
          </Alert>
        ) : null}

        {canEnterManualSo ? (
          <ManualSoNumberFields
            soNumber={soNumber}
            reason={soReason}
            onSoNumberChange={setSoNumber}
            onReasonChange={setSoReason}
            disabled={submitting}
          />
        ) : null}

        <Autocomplete
          options={vendorOptions}
          loading={vendorLoading}
          value={selectedVendor}
          inputValue={vendorInput}
          onInputChange={(_e, value) => setVendorInput(value)}
          onChange={(_e, value) => setSelectedVendor(value)}
          getOptionLabel={formatVendorLabel}
          isOptionEqualToValue={(a, b) =>
            String(a?.vendor_id || '') === String(b?.vendor_id || '')
          }
          disabled={submitting}
          sx={{ mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              size="small"
              label="Vendor"
              placeholder="Search vendors…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {vendorLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <DistributionCenterField
          options={locations}
          selected={selectedLocation}
          inputValue={locationInputValue}
          loading={locationsLoading}
          loadingMore={locationsLoadingMore}
          disabled={submitting}
          onSelect={selectLocation}
          onInputChange={handleLocationInputChange}
          onListScroll={handleLocationListScroll}
        />

        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  Ship now
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dsdLines.map((li) => {
                const remaining = getLineRemainingToShip(li)
                const articleCode = getArticleCode(li)
                return (
                  <TableRow key={li.id}>
                    <TableCell>
                      {li.product_name || `Line ${li.id}`}
                      {articleCode ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Article Code: {articleCode}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{remaining}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        sx={{ width: '100%', minWidth: 96 }}
                        inputProps={{ min: 0, step: 1 }}
                        value={qtyByLine[li.id] ?? ''}
                        disabled={submitting}
                        onChange={(e) =>
                          setQtyByLine((prev) => ({
                            ...prev,
                            [li.id]: normalizeShipQty(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
              {pendingAdds.map((add) => (
                <TableRow key={add.key}>
                  <TableCell>
                    {add.product_name || 'Added product'}
                    {add.sku ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Article Code: {add.sku}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      sx={{ width: '100%', minWidth: 96 }}
                      inputProps={{ min: 0, step: 1 }}
                      value={add.quantity}
                      disabled={submitting}
                      onChange={(e) => {
                        const next = normalizeShipQty(e.target.value)
                        setPendingAdds((prev) =>
                          prev.map((row) =>
                            row.key === add.key
                              ? { ...row, quantity: next }
                              : row,
                          ),
                        )
                      }}
                      helperText={
                        validatePositiveSellingPriceAndMrp(
                          Number(add.unit_price),
                          Number(add.mrp),
                          { requireBoth: true },
                        ) ||
                        (Number(add.quantity) > 0
                          ? formatSellingPriceExceedsMrpError(
                              add.unit_price,
                              add.mrp,
                            )
                          : undefined)
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              {dsdLines.length === 0 && pendingAdds.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Alert severity="warning">
                      No eligible DSD products remain for this shipment.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? (
            <CircularProgress size={22} />
          ) : (
            'Create Fulfillment Center assignment'
          )}
        </Button>
      </DialogActions>
    </Dialog>

    <ShipmentVatReviewDialog
      open={vatReviewOpen}
      onClose={() => {
        if (submitting) return
        setVatReviewOpen(false)
        setVatReviewError(null)
      }}
      rows={vatReviewRows}
      submitting={submitting}
      submitError={vatReviewError}
      onVatUpdated={handleVatUpdated}
      onConfirm={async () => {
        await executeCreate()
      }}
    />
    </>
  )
}
